import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadForm({
  interest = "both",
  source = "inline",
  city = null,
  variant = "light",
  ctaLabel = "Send Me Updates",
  outboundUrl = null,
  testIdPrefix = "lead",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email,
        name: name || null,
        interest,
        city,
        source,
      });
      setDone(true);
      toast.success("You're on the list.");
      // Outbound CTA after lead capture
      if (outboundUrl) {
        setTimeout(() => window.open(outboundUrl, "_blank"), 600);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const labelCls = isDark
    ? "text-xs tracking-widest uppercase text-white/60 mb-2 block"
    : "text-xs tracking-widest uppercase text-gray-500 mb-2 block";
  const inputCls = isDark
    ? "border-b border-white/30 bg-transparent text-white rounded-none px-0 py-3 focus:border-white focus:outline-none w-full font-sans placeholder:text-white/40"
    : "border-b border-black/20 bg-transparent text-[#111] rounded-none px-0 py-3 focus:border-black focus:outline-none w-full font-sans placeholder:text-black/30";

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={
          isDark
            ? "border-l-2 border-white/40 pl-6 py-2"
            : "border-l-2 border-black/40 pl-6 py-2"
        }
      >
        <p className="font-serif-display text-2xl mb-1">Thanks — you&apos;re in.</p>
        <p className={isDark ? "text-white/60 text-sm" : "text-gray-600 text-sm"}>
          We&apos;ll send you practical, no-spam updates.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      className="space-y-6 max-w-md"
    >
      <div>
        <label className={labelCls}>Name (optional)</label>
        <input
          type="text"
          data-testid={`${testIdPrefix}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Reporter"
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>Work Email</label>
        <input
          type="email"
          required
          data-testid={`${testIdPrefix}-email`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@firm.com"
          className={inputCls}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testIdPrefix}-submit`}
        className={
          (isDark
            ? "bg-white text-black hover:bg-white/90"
            : "bg-black text-white hover:bg-gray-900") +
          " btn-sharp inline-flex items-center gap-2 disabled:opacity-50"
        }
      >
        {loading && <Loader2 size={14} className="animate-spin" />}
        {ctaLabel}
      </button>
    </form>
  );
}
